import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Briefcase, TrendingUp, Globe, FileText, ArrowRight, ChevronRight, CheckCircle2, Building, Plane, Scale, Cog, Users, Handshake, Megaphone, Award, Cpu, Landmark, Wrench, HeartPulse, Leaf } from 'lucide-react';
import CTASection from '../components/CTASection';
import GetStartedModal from '../components/GetStartedModal';
import CallbackModal from '../components/CallbackModal';

const iconMap = { Briefcase, TrendingUp, Globe, FileText, Building, Plane, Scale, Cog, Users, Handshake, Megaphone, Award, Cpu, Landmark, Wrench, HeartPulse, Leaf };

const Home = () => {
  const { t } = useLang();
  const [gs, setGs] = useState(false);
  const [cb, setCb] = useState(false);
  const heroIcons = [Briefcase, TrendingUp, Globe, FileText];
  const serviceIcons = [Building, Plane, Landmark, Scale, Cog, Handshake, Megaphone, Award];

  return (
    <div>
      {/* HERO */}
      <section className="hero-green text-white">
        <div className="sib-container py-16 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow text-[#D4A840] mb-4">{t.common.tagline}</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.08] mb-5" style={{fontFamily:"'Playfair Display',serif"}}>{t.hero.title}</h1>
            <p className="text-white/85 text-[16px] leading-relaxed max-w-xl mb-7">{t.hero.desc}</p>
            <div className="flex flex-wrap gap-3 mb-6">
              <button onClick={() => setGs(true)} className="btn-gold">{t.common.startJourney}<ArrowRight size={16} className="rtl-flip" /></button>
              <Link to="/opportunities" className="btn-gold-outline">{t.common.exploreOpp}</Link>
            </div>
            <div className="text-[13px] text-[#D4A840]/90 tracking-wide">{t.hero.footerLine}</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {t.hero.cards.map((c, i) => {
              const Icon = heroIcons[i];
              return (
                <Link key={i} to={c.link} className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 text-center card-hover hover:bg-white/10">
                  <div className="w-12 h-12 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-3 group-hover:bg-[#D4A840]/20 transition"><Icon className="w-6 h-6 text-[#D4A840]" /></div>
                  <div className="font-semibold text-[15px]">{c.title}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* BRIDGE */}
      <section className="bg-[#FBF9F2] py-20">
        <div className="sib-container text-center max-w-3xl mx-auto">
          <div className="eyebrow mb-3">{t.bridge.eyebrow}</div>
          <h2 className="section-title mb-5">{t.bridge.title}</h2>
          <p className="text-[#33473d] leading-relaxed">{t.bridge.desc}</p>
        </div>
      </section>

      {/* WHO WE HELP */}
      <section className="bg-white py-20">
        <div className="sib-container">
          <h2 className="section-title text-center mb-12">{t.whoWeHelp.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.whoWeHelp.items.map((it, i) => (
              <div key={i} className="bg-[#FBF9F2] border border-[#E8E1CE] rounded-lg p-6 card-hover">
                <div className="w-11 h-11 rounded-md bg-[#0B5D3B]/10 flex items-center justify-center mb-4">
                  {[Users, TrendingUp, Globe, FileText][i] && React.createElement([Users, TrendingUp, Globe, FileText][i], { className: 'w-5 h-5 text-[#0B5D3B]' })}
                </div>
                <h3 className="text-lg font-semibold text-[#0B5D3B] mb-2">{it.title}</h3>
                <p className="text-[14px] text-[#33473d]/85 leading-relaxed mb-4">{it.desc}</p>
                <Link to={it.link} className="text-[#0B5D3B] text-sm font-semibold inline-flex items-center gap-1 hover:text-[#D4A840]">{it.cta} <ChevronRight size={14} className="rtl-flip" /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#FBF9F2] py-20">
        <div className="sib-container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="eyebrow mb-3">{t.services.eyebrow}</div>
            <h2 className="section-title">{t.services.title}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.services.list.map((s, i) => {
              const Icon = serviceIcons[i] || Briefcase;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="bg-white border border-[#E8E1CE] rounded-lg p-5 card-hover group">
                  <div className="w-11 h-11 rounded-md bg-[#D4A840]/15 flex items-center justify-center mb-3 group-hover:bg-[#D4A840]/25 transition"><Icon className="w-5 h-5 text-[#D4A840]" /></div>
                  <h4 className="font-semibold text-[#0B5D3B] mb-2 text-[15px]">{s.title}</h4>
                  <p className="text-[13px] text-[#33473d]/80 leading-relaxed">{s.short}</p>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="btn-green">{t.services.exploreAll}<ArrowRight size={16} className="rtl-flip" /></Link>
          </div>
        </div>
      </section>

      {/* TENDER */}
      <section className="bg-white py-20">
        <div className="sib-container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="eyebrow mb-3">{t.tender.eyebrow}</div>
            <h2 className="section-title mb-4">{t.tender.title}</h2>
            <p className="text-[#33473d]/85">{t.tender.desc}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {t.tender.steps.map((s, i) => (
              <div key={i} className="bg-[#FBF9F2] border border-[#E8E1CE] rounded-lg p-6 card-hover">
                <div className="text-[#D4A840] font-bold text-2xl mb-2" style={{fontFamily:"'Playfair Display',serif"}}>{String(i+1).padStart(2,'0')}</div>
                <h4 className="font-semibold text-[#0B5D3B] mb-1.5 text-[15px]">{s.title}</h4>
                <p className="text-[13px] text-[#33473d]/80 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center flex flex-wrap gap-3 justify-center">
            <button onClick={() => setGs(true)} className="btn-gold">{t.tender.cta}</button>
            <Link to="/tender-support" className="btn-green-outline">{t.tender.how}</Link>
          </div>
        </div>
      </section>

      {/* INVESTORS */}
      <section className="bg-[#FBF9F2] py-20">
        <div className="sib-container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="section-title mb-4">{t.investors.title}</h2>
            <p className="text-[#33473d]/85">{t.investors.desc}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[t.investors.seeking, t.investors.looking].map((b, i) => (
              <div key={i} className="bg-white border border-[#E8E1CE] rounded-lg p-8 card-hover">
                <div className="w-11 h-11 rounded-md bg-[#0B5D3B]/10 flex items-center justify-center mb-4">{i === 0 ? <TrendingUp className="w-5 h-5 text-[#0B5D3B]" /> : <Handshake className="w-5 h-5 text-[#0B5D3B]" />}</div>
                <h3 className="text-xl font-semibold text-[#0B5D3B] mb-2">{b.title}</h3>
                <p className="text-[14px] text-[#33473d]/85 mb-5 leading-relaxed">{b.desc}</p>
                <button onClick={() => setGs(true)} className="btn-green text-sm">{b.cta}<ArrowRight size={14} className="rtl-flip" /></button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY SAUDI */}
      <section className="relative py-24 bg-cover bg-center" style={{ backgroundImage: `linear-gradient(rgba(11,93,59,0.85), rgba(11,93,59,0.9)), url('https://images.pexels.com/photos/37074462/pexels-photo-37074462.jpeg?auto=compress&cs=tinysrgb&w=1920')` }}>
        <div className="sib-container text-center text-white max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{fontFamily:"'Playfair Display',serif"}}>{t.whySaudi.title}</h2>
          <p className="text-white/85 mb-10 leading-relaxed">{t.whySaudi.desc}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            {t.whySaudi.sectors.map((s, i) => (
              <span key={i} className="px-4 py-2 border border-white/25 rounded-full text-sm bg-white/5 backdrop-blur-sm">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-20">
        <div className="sib-container">
          <h2 className="section-title text-center mb-12">{t.process.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.process.steps.map((s, i) => (
              <div key={i} className="relative bg-[#FBF9F2] border border-[#E8E1CE] rounded-lg p-6 card-hover">
                <div className="text-[#D4A840] font-bold text-3xl mb-2" style={{fontFamily:"'Playfair Display',serif"}}>{s.n}</div>
                <h4 className="font-semibold text-[#0B5D3B] mb-2 text-[15px]">{s.title}</h4>
                <p className="text-[13px] text-[#33473d]/80 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSPIRATION */}
      <section className="bg-[#FBF9F2] py-20">
        <div className="sib-container text-center max-w-3xl mx-auto">
          <div className="eyebrow mb-3">{t.inspiration.eyebrow}</div>
          <h2 className="section-title mb-5">{t.inspiration.title}</h2>
          <p className="text-[#33473d]/85 leading-relaxed mb-4">{t.inspiration.desc}</p>
          <p className="text-xs text-[#33473d]/60 italic max-w-2xl mx-auto">{t.inspiration.disclaimer}</p>
        </div>
      </section>

      <CTASection />
      <GetStartedModal open={gs} onClose={() => setGs(false)} pageSource="home" />
      <CallbackModal open={cb} onClose={() => setCb(false)} pageSource="home" />
    </div>
  );
};

export default Home;
